import type { ColumnDef } from "@tanstack/react-table";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { PayrollStatementNotificationRecipient } from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import {
  formatTextNumber,
  getNotificationReasonLabel,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/utils";

/**
 * Where the PayPack goes. The mobile line only appears for a business that texts
 * PayPacks, and then says why a technician won't be texted rather than going blank.
 */
const TextLine = ({
  recipient,
}: {
  recipient: PayrollStatementNotificationRecipient;
}) => {
  // Owners are already marked preview-only on the row; saying it twice is noise.
  if (recipient.sms_ineligible_reason === "owner_preview_only") return null;
  if (recipient.sms_ineligible_reason)
    return (
      <Typography variant="captionW1" textColor="tertiary">
        {getNotificationReasonLabel(recipient.sms_ineligible_reason)}
      </Typography>
    );
  if (!recipient.phone_number) return null;

  return (
    <Typography variant="captionW1" textColor="tertiary">
      {formatTextNumber(recipient.phone_number)}
    </Typography>
  );
};

export const ContactCell = ({
  recipient,
}: {
  recipient: PayrollStatementNotificationRecipient;
}) => (
  <Stack gap="xs" className="min-w-0">
    {recipient.email ? (
      <Typography variant="bodyW3" className="truncate">
        {recipient.email}
      </Typography>
    ) : (
      <Typography variant="bodyW3" textColor="tertiary">
        No saved email
      </Typography>
    )}
    <TextLine recipient={recipient} />
  </Stack>
);

export const EmailCol = (): ColumnDef<
  PayrollStatementNotificationRecipient,
  unknown
> => ({
  id: "email",
  enableSorting: false,
  header: () => <span>Email</span>,
  cell: ({ row }) => <ContactCell recipient={row.original} />,
});
